import React, { useEffect, useState, createContext, useContext } from 'react';
export type CartItemType = 'book' | 'supplement';
export interface CartItem {
  productId: string;
  name: string;
  unitPrice: number; // in cents
  quantity: number;
  type: CartItemType;
  /** Stripe Price ID — used if the product has a stable Price ID */
  stripePriceId?: string;
  /** Stripe price lookup_key — resolves to whichever active price has it */
  lookupKey?: string;
  /** Optional thumbnail label or color for rendering in cart drawer */
  accent?: string;
}
interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: number; // in cents
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (item: Omit<CartItem, 'quantity'>, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
}
const STORAGE_KEY = 'jackachenbach.cart.v1';
const CartContext = createContext<CartContextValue | undefined>(undefined);
function loadFromStorage(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((i: any): i is CartItem => i && typeof i.productId === 'string' && typeof i.name === 'string' && typeof i.unitPrice === 'number' && typeof i.quantity === 'number' && (i.type === 'book' || i.type === 'supplement')).map((i: CartItem) => ({
      productId: i.productId,
      name: i.name,
      unitPrice: i.unitPrice,
      quantity: i.quantity,
      type: i.type,
      stripePriceId: (i as any).stripePriceId,
      lookupKey: (i as any).lookupKey,
      accent: (i as any).accent
    }));
  } catch {
    return [];
  }
}
function saveToStorage(items: CartItem[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // ignore quota errors
  }
}
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [items, setItems] = useState<CartItem[]>(() => loadFromStorage());
  const [isOpen, setIsOpen] = useState(false);
  // Persist on every change
  useEffect(() => {
    saveToStorage(items);
  }, [items]);
  // Sync across tabs
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setItems(loadFromStorage());
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.unitPrice * i.quantity, 0);
  const value: CartContextValue = {
    items,
    itemCount,
    subtotal,
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    toggleCart: () => setIsOpen(v => !v),
    addItem: (item, quantity = 1) => {
      setItems(prev => {
        const existing = prev.find(i => i.productId === item.productId);
        if (existing) {
          return prev.map(i => i.productId === item.productId ? {
            ...i,
            quantity: i.quantity + quantity
          } : i);
        }
        return [...prev, {
          ...item,
          quantity
        }];
      });
      setIsOpen(true);
    },
    removeItem: productId => {
      setItems(prev => prev.filter(i => i.productId !== productId));
    },
    updateQuantity: (productId, quantity) => {
      if (quantity <= 0) {
        setItems(prev => prev.filter(i => i.productId !== productId));
        return;
      }
      setItems(prev => prev.map(i => i.productId === productId ? {
        ...i,
        quantity
      } : i));
    },
    clearCart: () => setItems([])
  };
  return <CartContext.Provider value={value} data-id="element-2716">{children}</CartContext.Provider>;
}
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside <CartProvider>');
  return ctx;
}